import { useRef, useState } from 'react';
import { C } from '../theme';
import { Icon } from '../icons';
import { Loading, Empty, Spinner } from '../components/States';
import { useToast } from '../components/Toast';
import { api, ApiError } from '../api';
import type { DocumentRow, MyTenancy } from '../api';
import { useAsync } from '../useAsync';
import { fmtDate } from '../format';

const KIND_LABEL: Record<string, string> = {
  lease: 'Lease',
  gst: 'GST',
  invoice: 'Invoice',
  receipt: 'Receipt',
  insurance: 'Insurance',
  noc: 'NOC',
  id: 'ID',
  other: 'Document',
};

/** Kinds a tenant is likely to file themselves. */
const TENANT_KINDS: Array<[string, string]> = [
  ['id', 'ID (Aadhaar / PAN)'],
  ['lease', 'Lease'],
  ['receipt', 'Receipt'],
  ['insurance', 'Insurance'],
  ['other', 'Other'],
];

/** Tenant documents (GET /my/documents) — owner-shared docs + anything the tenant uploads. */
export function TenantDocs() {
  const notify = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<string>('id');

  const { data, loading, error, reload } = useAsync<DocumentRow[]>(() => api.myDocuments(), []);
  // Resolve the caller's tenancy id so an upload can be scoped to it (fails soft).
  const { data: tenancy } = useAsync<MyTenancy>(() => api.myTenancy(), []);
  const tenancyId = tenancy?.tenancyId;

  async function onPick(file: File | undefined) {
    if (!file) return;
    if (!tenancyId) {
      notify('Still loading your tenancy. Try again in a moment.');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tenancyId', tenancyId);
      fd.append('kind', kind);
      fd.append('title', file.name);
      await api.uploadDocument(fd);
      notify('Document uploaded');
      reload();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Upload failed. Try again.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function openDoc(id: string) {
    window.open(api.documentContentUrl(id), '_blank', 'noopener');
  }

  // The upload control sits above whatever list state we're in.
  const uploader = (
    <div style={{ display: 'flex', gap: 8 }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={(e) => onPick(e.target.files?.[0])}
      />
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value)}
        aria-label="Document type"
        style={{ flex: 'none', border: `1.5px solid ${C.line}`, borderRadius: 12, padding: '0 10px', background: C.card, color: C.ink2, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}
      >
        {TENANT_KINDS.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
      </select>
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy || !tenancyId}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          background: C.card,
          border: `1.5px dashed ${busy || !tenancyId ? C.line : '#cdc4bb'}`,
          borderRadius: 12,
          padding: '13px 12px',
          color: busy || !tenancyId ? C.muted : C.accent2,
          fontSize: 13.5,
          fontWeight: 600,
          cursor: busy || !tenancyId ? 'default' : 'pointer',
        }}
      >
        {busy ? <Spinner size={15} /> : <Icon n="doc" size={16} />}
        {busy ? 'Uploading…' : 'Upload a document'}
      </button>
    </div>
  );

  if (loading) return <Loading label="Loading documents…" minHeight={300} />;

  if (error) {
    return (
      <>
        {uploader}
        <Empty icon="doc" title="Documents unavailable" hint="Please try again in a moment." minHeight={220}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      </>
    );
  }

  if (!data || data.length === 0) {
    return (
      <>
        {uploader}
        <Empty
          icon="doc"
          title="No documents yet"
          hint="Documents your owner shares, and anything you upload, will appear here."
          minHeight={220}
        />
      </>
    );
  }

  return (
    <>
      {uploader}
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, overflow: 'hidden' }}>
        {data.map((d, i) => {
          const sub =
            d.subtitle ??
            [KIND_LABEL[d.kind] ?? 'Document', d.createdAt ? `Shared ${fmtDate(d.createdAt)}` : null, (d.mime?.includes('pdf') ? 'PDF' : d.mime)]
              .filter(Boolean)
              .join(' · ');
          return (
            <button
              key={d.id}
              onClick={() => openDoc(d.id)}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 11, padding: '13px 14px', borderTop: i ? `1px solid ${C.line2}` : 0, background: 'transparent', border: 0, borderTopWidth: i ? 1 : 0, cursor: 'pointer' }}
            >
              <span style={{ width: 32, height: 32, borderRadius: 8, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <Icon n="doc" size={16} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b style={{ fontSize: 13.5, color: C.ink, fontWeight: 600, display: 'block' }}>{d.title}</b>
                <small style={{ fontSize: 12, color: C.muted }}>{sub}</small>
              </div>
              <span style={{ fontSize: 10.5, fontWeight: 600, color: d.mine ? C.accent2 : C.hi, background: d.mine ? C.soft : C.greenSoft, borderRadius: 999, padding: '3px 8px', whiteSpace: 'nowrap' }}>{d.mine ? 'You' : 'From owner'}</span>
              <Icon n="chev" size={15} color={C.muted} />
            </button>
          );
        })}
      </div>
    </>
  );
}

const retryBtn: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
